import { useMutation } from '@tanstack/react-query';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { serviceQualification } from '@services/serviceQualificationService';
import { qualificationRouter } from '@utils/qualificationRouter/qualificationRouter';
import type {
  ServiceQualificationRequest,
  ServiceQualificationResult,
  QualificationRouterInput,
  QualificationRouteResult,
} from '@t/serviceQualification.types';
import { CPQ_QUALIFICATION_CACHE_KEY } from '@t/session.types';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';

interface QualificationCacheEntry {
  addressKey: string;
  result: ServiceQualificationResult;
}

interface UseServiceQualificationOptions {
  onRoute?: (route: QualificationRouteResult) => void;
  onError?: (error: Error) => void;
}

/**
 * Builds a deterministic cache key from the address fields sent to the API.
 */
const buildAddressKey = (payload: ServiceQualificationRequest): string => {
  const parts = [
    payload.address.unitNumber,
    payload.address.streetNumber,
    payload.address.streetName,
    payload.address.city,
    payload.address.province,
    payload.address.postalCode,
    payload.address.country,
  ];
  return parts.map((p) => (p ?? '').trim().toLowerCase()).join('|');
};

export const useServiceQualification = ({ onRoute, onError }: UseServiceQualificationOptions = {}) => {
  const [cachedEntry, setCachedEntry] = useLocalStorage<QualificationCacheEntry | null>(
    CPQ_QUALIFICATION_CACHE_KEY,
    null
  );
  const setQualificationFlow = useQualificationFlowStore((s) => s.setQualificationFlow);

  /**
   * Applies the router's store-update payload to the qualification flow atom
   * and returns the bare routing result for consumers. REQUEST_UNIT carries
   * no payload, so the store stays untouched for parent addresses.
   */
  const processResult = (input: QualificationRouterInput): QualificationRouteResult => {
    const { storeUpdate, ...route } = qualificationRouter(input);

    if (storeUpdate) {
      setQualificationFlow((prev) => ({ ...prev, ...storeUpdate }));
    }

    return route;
  };

  const mutation = useMutation<
    ServiceQualificationResult,
    Error,
    { sessionId: string; payload: ServiceQualificationRequest }
  >({
    mutationFn: ({ sessionId, payload }) => serviceQualification(sessionId, payload),
    onSuccess: (result, variables) => {
      // Cache the successful response keyed by the normalized address
      setCachedEntry({
        addressKey: buildAddressKey(variables.payload),
        result,
      });
      const route = processResult(result);
      onRoute?.(route);
    },
    onError: (error) => {
      // AMS-404: treat as routing outcome → Lead Capture (no error UI).
      // The store write must happen even when no onRoute consumer is wired,
      // so processResult cannot sit inside the optional call's argument list.
      if (error.name === 'AMS-404') {
        const route = processResult('AMS-404');
        onRoute?.(route);
        return;
      }

      // All other errors → consumer handles inline error state
      onError?.(error);
    },
  });

  const qualify = (
    sessionId: string,
    request: ServiceQualificationRequest,
    options?: { forceFresh?: boolean }
  ): void => {
    const addressKey = buildAddressKey(request);

    if (!options?.forceFresh && cachedEntry && cachedEntry.addressKey === addressKey) {
      const route = processResult(cachedEntry.result);
      onRoute?.(route);
      return;
    }

    setCachedEntry(null);
    mutation.mutate({ sessionId, payload: request });
  };

  return {
    qualify,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: cachedEntry?.result ?? mutation.data,
    reset: mutation.reset,
  };
};
